import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faDroplet,
  faClock,
  faCalendarDay,
  faPlus,
  faPen,
  faTrash,
  faCheck,
  faExclamationTriangle,
  faBell,
  faNotesMedical,
  faChevronRight,
  faSeedling,
  faHeart,
  faSpinner,
  faCalendarDays,
  faVenus,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import womensHealthService, {
  CycleEntry,
  WomanProfile,
  Symptom,
} from '../../../services/womensHealthService';
import BackButton from '../../../components/BackButton/BackButton';
import Modal from '../../../components/Modal/Modal';
import ProfileOverviewCard from '../../../components/ProfileOverviewCard/ProfileOverviewCard';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import './CycleTracker.css';
import { useProfile } from '../../../context/ProfileContext';

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

type TabKey = 'cycles' | 'log';

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info';
}

interface ChoiceOption {
  value: string;
  display_name: string;
}

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const formatDate = (iso: string | null): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const daysBetween = (a: string, b: string): number => {
  const d1 = new Date(a);
  const d2 = new Date(b);
  return Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
};

const toInputDate = (iso: string): string => {
  if (!iso) return '';
  return iso.split('T')[0];
};

const FLOW_COLORS: Record<string, string> = {
  light: '#93c5fd',
  medium: '#f472b6',
  heavy: '#ef4444',
  spotting: '#d8b4fe',
};

/* ================================================================== */
/*  Toast Component                                                    */
/* ================================================================== */

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const iconMap: Record<string, IconDefinition> = {
    success: faCheck,
    error: faExclamationTriangle,
    info: faBell,
  };

  return (
    <div className={`wh-cycle-toast wh-cycle-toast-${type}`}>
      <span className="wh-cycle-toast-icon">
        <FontAwesomeIcon icon={iconMap[type]} />
      </span>
      <span className="wh-cycle-toast-message">{message}</span>
      <button className="wh-cycle-toast-close" onClick={onClose} aria-label="Close notification">
        {'\u00D7'}
      </button>
    </div>
  );
};

/* ================================================================== */
/*  Skeleton Loader                                                    */
/* ================================================================== */

const SkeletonCards: React.FC = () => (
  <div className="wh-cycle-skeleton-grid">
    {[0, 1, 2].map((i) => (
      <div key={i} className="wh-cycle-skeleton-card">
        <div className="wh-cycle-skeleton-line long" />
        <div className="wh-cycle-skeleton-line medium" />
        <div className="wh-cycle-skeleton-line short" />
        <div className="wh-cycle-skeleton-line medium" />
      </div>
    ))}
  </div>
);

/* ================================================================== */
/*  Initial Form State                                                 */
/* ================================================================== */

const INITIAL_FORM = {
  last_period_start: '',
  avg_cycle_length: 28,
  avg_period_length: 5,
  flow_intensity: '',
  symptoms: [] as number[],
  notes: '',
};

const INITIAL_PROFILE_FORM = {
  age: '' as string | number,
  lifecycle_stage: 'puberty',
};

/* ================================================================== */
/*  Main Component                                                     */
/* ================================================================== */

const CycleTracker: React.FC = () => {
  const { t } = useTranslation(['womensHealth', 'common']);
  const { activeProfile } = useProfile();
  const [searchParams] = useSearchParams();

  // Data state
  const [cycles, setCycles] = useState<CycleEntry[]>([]);
  const [latestCycle, setLatestCycle] = useState<CycleEntry | null>(null);
  const [symptoms, setSymptoms] = useState<Symptom[]>([]);
  const [flowChoices, setFlowChoices] = useState<ChoiceOption[]>([]);

  // Profile state
  const [profile, setProfile] = useState<WomanProfile | null>(null);
  const [profileChoices, setProfileChoices] = useState<Record<string, ChoiceOption[]>>({});
  const [profileForm, setProfileForm] = useState(INITIAL_PROFILE_FORM);
  const [editingProfile, setEditingProfile] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>((searchParams.get('tab') as TabKey) || 'cycles');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  // Toast helper
  const showToast = useCallback((message: string, type: ToastState['type']) => {
    setToast({ message, type });
  }, []);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [cyclesData, latest, symptomsData, choices, prof, profChoices] = await Promise.all([
        womensHealthService.getCycles(),
        womensHealthService.getCycleLatest(),
        womensHealthService.getSymptomsByCategory('menstrual'),
        womensHealthService.getCycleChoices(),
        womensHealthService.getProfileLatest(),
        womensHealthService.getProfileChoices(),
      ]);
      setCycles(Array.isArray(cyclesData) ? cyclesData : []);
      setLatestCycle(latest);
      setSymptoms(Array.isArray(symptomsData) ? symptomsData : []);
      if (choices?.flow_intensity) {
        setFlowChoices(choices.flow_intensity);
      }
      setProfile(prof);
      if (profChoices) setProfileChoices(profChoices);
    } catch (err) {
      console.error('Failed to fetch cycle data:', err);
      showToast('Failed to load cycle data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData, activeProfile]);

  /* ---------------------------------------------------------------- */
  /*  Profile Setup / Edit                                             */
  /* ---------------------------------------------------------------- */

  const handleProfileSubmit = async () => {
    if (profileForm.age === '' || profileForm.age === undefined) {
      showToast('Please enter your age.', 'error');
      return;
    }
    try {
      setSaving(true);
      const payload: any = {
        age: Number(profileForm.age),
        lifecycle_stage: profileForm.lifecycle_stage,
      };
      if (editingProfile && profile) {
        await womensHealthService.updateProfile(profile.id, payload);
        showToast('Profile updated!', 'success');
      } else {
        await womensHealthService.createProfile(payload);
        showToast('Profile created!', 'success');
      }
      setEditingProfile(false);
      await fetchData();
    } catch (err) {
      console.error('Error saving profile:', err);
      showToast('Failed to save profile.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const startEditProfile = () => {
    if (!profile) return;
    setProfileForm({
      age: profile.age ?? '',
      lifecycle_stage: profile.lifecycle_stage,
    });
    setEditingProfile(true);
  };

  const cancelEditProfile = () => {
    setEditingProfile(false);
    setProfileForm(INITIAL_PROFILE_FORM);
  };

  // Computed stats from latest cycle
  const stats = useMemo(() => {
    if (!latestCycle) {
      return {
        currentDay: '--',
        daysUntil: '--',
        cycleLength: '--',
        flowIntensity: '--',
      };
    }

    const now = new Date();
    const currentDay = Math.max(1, daysBetween(latestCycle.last_period_start, now.toISOString().split('T')[0]) + 1);

    let daysUntil = '--';
    if (latestCycle.next_period_start) {
      const diff = daysBetween(now.toISOString().split('T')[0], latestCycle.next_period_start);
      daysUntil = diff >= 0 ? String(diff) : 'Due';
    }

    return {
      currentDay: String(currentDay),
      daysUntil,
      cycleLength: String(latestCycle.avg_cycle_length),
      flowIntensity: latestCycle.flow_intensity_display || latestCycle.flow_intensity || '--',
    };
  }, [latestCycle]);

  // Form handlers
  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM);
    setEditingId(null);
  }, []);

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSymptomToggle = (symptomId: number) => {
    setFormData((prev) => {
      const exists = prev.symptoms.includes(symptomId);
      return {
        ...prev,
        symptoms: exists
          ? prev.symptoms.filter((id) => id !== symptomId)
          : [...prev.symptoms, symptomId],
      };
    });
  };

  const handleEdit = (cycle: CycleEntry) => {
    setFormData({
      last_period_start: toInputDate(cycle.last_period_start),
      avg_cycle_length: cycle.avg_cycle_length,
      avg_period_length: cycle.avg_period_length,
      flow_intensity: cycle.flow_intensity,
      symptoms: cycle.symptoms || [],
      notes: cycle.notes || '',
    });
    setEditingId(cycle.id);
    setActiveTab('log');
  };

  const handleSave = async () => {
    if (!formData.last_period_start) {
      showToast('Please select the last period start date.', 'error');
      return;
    }
    if (!formData.flow_intensity) {
      showToast('Please select a flow intensity.', 'error');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        last_period_start: formData.last_period_start,
        avg_cycle_length: formData.avg_cycle_length,
        avg_period_length: formData.avg_period_length,
        flow_intensity: formData.flow_intensity,
        symptoms: formData.symptoms,
        notes: formData.notes.trim(),
      };

      if (editingId !== null) {
        await womensHealthService.updateCycle(editingId, payload);
        showToast('Cycle entry updated successfully!', 'success');
      } else {
        await womensHealthService.createCycle(payload);
        showToast('Cycle entry created successfully!', 'success');
      }

      resetForm();
      await fetchData();
      setActiveTab('cycles');
    } catch (err) {
      console.error('Error saving cycle:', err);
      showToast('Failed to save cycle entry. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await womensHealthService.deleteCycle(id);
      setCycles((prev) => prev.filter((c) => c.id !== id));
      if (latestCycle?.id === id) {
        setLatestCycle(null);
      }
      showToast('Cycle entry deleted.', 'success');
      setConfirmDelete(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting cycle:', err);
      showToast('Failed to delete cycle entry.', 'error');
    }
  };

  // Tab config
  const tabs: { key: TabKey; label: string; icon: IconDefinition }[] = [
    { key: 'cycles', label: 'My Cycles', icon: faCalendarAlt },
    { key: 'log', label: editingId !== null ? 'Edit Cycle' : 'Log Cycle', icon: faPlus },
  ];

  /* ================================================================ */
  /*  Render: Profile Setup                                            */
  /* ================================================================ */

  const renderProfileFieldRow = () => (
    <div className="wh-cycle-form-row">
      <div className="wh-cycle-form-group">
        <label className="wh-cycle-form-label">
          Age <span className="wh-cycle-required">*</span>
        </label>
        <input
          type="number"
          className="wh-cycle-form-input"
          min={9}
          max={60}
          placeholder="e.g. 24"
          value={profileForm.age}
          onChange={(e) => setProfileForm({ ...profileForm, age: e.target.value })}
        />
      </div>
      <div className="wh-cycle-form-group">
        <label className="wh-cycle-form-label">
          Lifecycle Stage <span className="wh-cycle-required">*</span>
        </label>
        <select
          className="wh-cycle-form-select"
          value={profileForm.lifecycle_stage}
          onChange={(e) => setProfileForm({ ...profileForm, lifecycle_stage: e.target.value })}
        >
          <option value="">Select stage...</option>
          {(profileChoices.lifecycle_stage || [
            { value: 'puberty', display_name: 'Puberty & Menstrual' },
            { value: 'pregnancy', display_name: 'Pregnancy' },
            { value: 'postpartum', display_name: 'Postpartum' },
            { value: 'menopause', display_name: 'Menopause' },
          ]).map((c) => (
            <option key={c.value} value={c.value}>{c.display_name}</option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderProfileSetup = () => (
    <div className="wh-cycle-setup-card">
      <div className="wh-cycle-setup-icon-wrap">
        <FontAwesomeIcon icon={faVenus} />
      </div>
      <h2 className="wh-cycle-setup-title">Set Up Your Profile</h2>
      <p className="wh-cycle-setup-desc">
        Tell us a little about yourself so we can personalize your cycle tracking and predictions.
      </p>
      <div className="wh-cycle-setup-form">
        {renderProfileFieldRow()}
        <div className="wh-cycle-setup-actions">
          <button
            type="button"
            className="wh-cycle-btn wh-cycle-btn-primary"
            onClick={handleProfileSubmit}
            disabled={saving}
          >
            {saving ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> Creating...</>
            ) : (
              <><FontAwesomeIcon icon={faCheck} /> Create Profile</>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Render: Profile Summary                                          */
  /* ================================================================ */

  const renderProfileSummary = () => {
    if (!profile) return null;
    return (
      <ProfileOverviewCard
        title="Your Profile"
        icon={faVenus}
        accentColor="#f472b6"
        rows={[
          { label: 'Age', value: profile.age ? `${profile.age} years` : '--' },
          { label: 'Lifecycle Stage', value: profile.lifecycle_stage_display || profile.lifecycle_stage || '--' },
        ]}
        onEdit={startEditProfile}
        editLabel="Edit"
      />
    );
  };

  /* ================================================================ */
  /*  Render: Stats Row                                                */
  /* ================================================================ */

  const renderStats = () => (
    <div className="wh-cycle-stats-row">
      <div className="wh-cycle-stat-card">
        <div className="wh-cycle-stat-icon" style={{ background: '#fce7f3', color: '#ec4899' }}>
          <FontAwesomeIcon icon={faCalendarDay} />
        </div>
        <div className="wh-cycle-stat-info">
          <span className="wh-cycle-stat-value">{stats.currentDay}</span>
          <span className="wh-cycle-stat-label">{t('womensHealth:cycleTracker.cycleDay', { day: '' })}</span>
        </div>
      </div>
      <div className="wh-cycle-stat-card">
        <div className="wh-cycle-stat-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}>
          <FontAwesomeIcon icon={faClock} />
        </div>
        <div className="wh-cycle-stat-info">
          <span className="wh-cycle-stat-value">{stats.daysUntil}</span>
          <span className="wh-cycle-stat-label">{t('womensHealth:cycleTracker.nextPeriod')}</span>
        </div>
      </div>
      <div className="wh-cycle-stat-card">
        <div className="wh-cycle-stat-icon" style={{ background: '#ede9fe', color: '#8b5cf6' }}>
          <FontAwesomeIcon icon={faCalendarDays} />
        </div>
        <div className="wh-cycle-stat-info">
          <span className="wh-cycle-stat-value">{stats.cycleLength}</span>
          <span className="wh-cycle-stat-label">{t('womensHealth:cycleTracker.cycleLength')}</span>
        </div>
      </div>
      <div className="wh-cycle-stat-card">
        <div className="wh-cycle-stat-icon" style={{ background: '#fce7f3', color: '#f472b6' }}>
          <FontAwesomeIcon icon={faDroplet} />
        </div>
        <div className="wh-cycle-stat-info">
          <span className="wh-cycle-stat-value">{stats.flowIntensity}</span>
          <span className="wh-cycle-stat-label">Flow Intensity</span>
        </div>
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Render: Cycle Cards                                              */
  /* ================================================================ */

  const renderCycleCards = () => {
    if (loading) return <SkeletonCards />;

    if (cycles.length === 0) {
      return (
        <div className="wh-cycle-empty-state">
          <div className="wh-cycle-empty-icon-wrap">
            <FontAwesomeIcon icon={faCalendarAlt} />
          </div>
          <h3>No cycle entries yet</h3>
          <p>Start tracking your menstrual cycle to get predictions, insights, and reminders.</p>
          <button
            className="wh-cycle-btn wh-cycle-btn-primary"
            onClick={() => { resetForm(); setActiveTab('log'); }}
          >
            <FontAwesomeIcon icon={faPlus} />
            {t('womensHealth:cycleTracker.logPeriod')}
          </button>
        </div>
      );
    }

    return (
      <div className="wh-cycle-cards-grid">
        {cycles.map((cycle, idx) => {
          const flowColor = FLOW_COLORS[cycle.flow_intensity] || '#f472b6';

          return (
            <div
              key={cycle.id}
              className="wh-cycle-entry-card"
              style={{ animationDelay: `${idx * 0.06}s` }}
            >
              <div className="wh-cycle-entry-top" style={{ borderLeftColor: flowColor }}>
                <div className="wh-cycle-entry-header">
                  <div className="wh-cycle-entry-date-badge">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <span>Started {formatDate(cycle.last_period_start)}</span>
                  </div>
                  <span
                    className="wh-cycle-flow-badge"
                    style={{ background: `${flowColor}18`, color: flowColor, borderColor: `${flowColor}40` }}
                  >
                    <FontAwesomeIcon icon={faDroplet} />
                    {cycle.flow_intensity_display || cycle.flow_intensity}
                  </span>
                </div>

                <div className="wh-cycle-entry-details">
                  <div className="wh-cycle-detail-row">
                    <span className="wh-cycle-detail-label">{t('womensHealth:cycleTracker.cycleLength')}</span>
                    <span className="wh-cycle-detail-value">{cycle.avg_cycle_length} days</span>
                  </div>
                  <div className="wh-cycle-detail-row">
                    <span className="wh-cycle-detail-label">{t('womensHealth:cycleTracker.periodLength')}</span>
                    <span className="wh-cycle-detail-value">{cycle.avg_period_length} days</span>
                  </div>
                </div>

                {/* Predictions */}
                {(cycle.next_period_start || cycle.ovulation_date || cycle.fertile_window_start) && (
                  <div className="wh-cycle-predictions">
                    <div className="wh-cycle-predictions-title">
                      <FontAwesomeIcon icon={faChevronRight} /> Predictions
                    </div>
                    <div className="wh-cycle-prediction-strip">
                      {cycle.next_period_start && (
                        <div className="wh-cycle-prediction-item wh-cycle-pred-period">
                          <FontAwesomeIcon icon={faDroplet} />
                          <div>
                            <span className="wh-cycle-pred-label">{t('womensHealth:cycleTracker.nextPeriod')}</span>
                            <span className="wh-cycle-pred-date">{formatDate(cycle.next_period_start)}</span>
                          </div>
                        </div>
                      )}
                      {cycle.ovulation_date && (
                        <div className="wh-cycle-prediction-item wh-cycle-pred-ovulation">
                          <FontAwesomeIcon icon={faSeedling} />
                          <div>
                            <span className="wh-cycle-pred-label">{t('womensHealth:cycleTracker.ovulationDay')}</span>
                            <span className="wh-cycle-pred-date">{formatDate(cycle.ovulation_date)}</span>
                          </div>
                        </div>
                      )}
                      {cycle.fertile_window_start && cycle.fertile_window_end && (
                        <div className="wh-cycle-prediction-item wh-cycle-pred-fertile">
                          <FontAwesomeIcon icon={faHeart} />
                          <div>
                            <span className="wh-cycle-pred-label">{t('womensHealth:cycleTracker.fertileWindow')}</span>
                            <span className="wh-cycle-pred-date">
                              {formatDate(cycle.fertile_window_start)} - {formatDate(cycle.fertile_window_end)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Symptoms */}
                {cycle.symptom_details && cycle.symptom_details.length > 0 && (
                  <div className="wh-cycle-symptoms-row">
                    {cycle.symptom_details.map((s) => (
                      <span key={s.id} className="wh-cycle-symptom-chip">{s.name}</span>
                    ))}
                  </div>
                )}

                {/* Notes */}
                {cycle.notes && (
                  <div className="wh-cycle-notes-preview">
                    <FontAwesomeIcon icon={faNotesMedical} />
                    <span>{cycle.notes}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="wh-cycle-entry-actions">
                <button
                  className="wh-cycle-action-btn wh-cycle-action-edit"
                  onClick={() => handleEdit(cycle)}
                  title="Edit entry"
                >
                  <FontAwesomeIcon icon={faPen} /> {t('common:buttons.edit')}
                </button>
                <button
                  className="wh-cycle-action-btn wh-cycle-action-delete"
                  onClick={() => setConfirmDelete(cycle.id)}
                  title="Delete entry"
                >
                  <FontAwesomeIcon icon={faTrash} /> {t('common:buttons.delete')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  /* ================================================================ */
  /*  Render: Log Form                                                 */
  /* ================================================================ */

  const renderForm = () => (
    <div className="wh-cycle-form-section">
      <div className="wh-cycle-form-grid">
        {/* Left panel - Core fields */}
        <div className="wh-cycle-form-panel">
          <div className="wh-cycle-form-panel-title">
            <FontAwesomeIcon icon={faCalendarAlt} />
            Cycle Details
          </div>

          <div className="wh-cycle-form-group">
            <label className="wh-cycle-form-label">
              {t('womensHealth:cycleTracker.periodStart')} <span className="wh-cycle-required">*</span>
            </label>
            <input
              type="date"
              className="wh-cycle-form-input"
              value={formData.last_period_start}
              onChange={(e) => handleFieldChange('last_period_start', e.target.value)}
            />
          </div>

          <div className="wh-cycle-form-row">
            <div className="wh-cycle-form-group">
              <label className="wh-cycle-form-label">Avg Cycle Length (days)</label>
              <input
                type="number"
                className="wh-cycle-form-input"
                min={18}
                max={45}
                value={formData.avg_cycle_length}
                onChange={(e) => handleFieldChange('avg_cycle_length', parseInt(e.target.value) || 28)}
              />
            </div>
            <div className="wh-cycle-form-group">
              <label className="wh-cycle-form-label">Avg Period Length (days)</label>
              <input
                type="number"
                className="wh-cycle-form-input"
                min={1}
                max={14}
                value={formData.avg_period_length}
                onChange={(e) => handleFieldChange('avg_period_length', parseInt(e.target.value) || 5)}
              />
            </div>
          </div>

          <div className="wh-cycle-form-group">
            <label className="wh-cycle-form-label">
              Flow Intensity <span className="wh-cycle-required">*</span>
            </label>
            <div className="wh-cycle-flow-options">
              {flowChoices.map((choice) => {
                const color = FLOW_COLORS[choice.value] || '#f472b6';
                const isActive = formData.flow_intensity === choice.value;
                return (
                  <button
                    key={choice.value}
                    type="button"
                    className={`wh-cycle-flow-option ${isActive ? 'active' : ''}`}
                    style={isActive ? { background: color, borderColor: color, color: '#fff' } : { borderColor: `${color}50`, color }}
                    onClick={() => handleFieldChange('flow_intensity', choice.value)}
                  >
                    <FontAwesomeIcon icon={faDroplet} />
                    {choice.display_name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="wh-cycle-form-group">
            <label className="wh-cycle-form-label">Notes</label>
            <textarea
              className="wh-cycle-form-textarea"
              placeholder="Any additional notes about this cycle..."
              rows={3}
              value={formData.notes}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
            />
          </div>
        </div>

        {/* Right panel - Symptoms */}
        <div className="wh-cycle-form-panel">
          <div className="wh-cycle-form-panel-title">
            <FontAwesomeIcon icon={faNotesMedical} />
            Symptoms
          </div>

          {symptoms.length === 0 ? (
            <p className="wh-cycle-form-hint">No symptoms available for this category.</p>
          ) : (
            <div className="wh-cycle-symptoms-grid">
              {symptoms.map((symptom) => {
                const checked = formData.symptoms.includes(symptom.id);
                return (
                  <label
                    key={symptom.id}
                    className={`wh-cycle-symptom-option ${checked ? 'active' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleSymptomToggle(symptom.id)}
                    />
                    <span className="wh-cycle-symptom-checkbox">
                      {checked && <FontAwesomeIcon icon={faCheck} />}
                    </span>
                    <span className="wh-cycle-symptom-name">{symptom.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Form actions */}
      <div className="wh-cycle-form-actions">
        <button
          type="button"
          className="wh-cycle-btn wh-cycle-btn-cancel"
          onClick={() => { resetForm(); setActiveTab('cycles'); }}
        >
          {t('common:buttons.cancel')}
        </button>
        <button
          type="button"
          className="wh-cycle-btn wh-cycle-btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
          ) : editingId !== null ? (
            <><FontAwesomeIcon icon={faCheck} /> Update Entry</>
          ) : (
            <><FontAwesomeIcon icon={faPlus} /> Save Entry</>
          )}
        </button>
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Main Render                                                      */
  /* ================================================================ */

  return (
    <div className="wh-cycle-page">
      {/* Toast */}
      {toast && (
        <div className="wh-cycle-toast-container">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      {/* Delete Entry Confirmation */}
      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete Cycle Entry?"
        message="Are you sure you want to delete this cycle entry? This action cannot be undone."
        confirmLabel={t('common:buttons.delete')}
        cancelLabel={t('common:buttons.cancel')}
        variant="danger"
        onConfirm={() => { if (confirmDelete !== null) handleDelete(confirmDelete); }}
        onClose={() => setConfirmDelete(null)}
      />

      {/* Edit Profile Modal */}
      <Modal
        open={editingProfile}
        onClose={cancelEditProfile}
        title="Edit Your Profile"
        icon={faVenus}
        size="md"
        footer={
          <>
            <button
              type="button"
              className="wh-cycle-btn wh-cycle-btn-cancel"
              onClick={cancelEditProfile}
              disabled={saving}
            >
              {t('common:buttons.cancel')}
            </button>
            <button
              type="button"
              className="wh-cycle-btn wh-cycle-btn-primary"
              onClick={handleProfileSubmit}
              disabled={saving}
            >
              {saving ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
              ) : (
                <><FontAwesomeIcon icon={faCheck} /> Save Changes</>
              )}
            </button>
          </>
        }
      >
        <div className="wh-cycle-setup-form">{renderProfileFieldRow()}</div>
      </Modal>

      {/* Hero Banner */}
      <section className="wh-cycle-hero">
        <div className="wh-cycle-hero-circle wh-cycle-hero-circle-1" />
        <div className="wh-cycle-hero-circle wh-cycle-hero-circle-2" />
        <Container>
          <BackButton />
          <div className="wh-cycle-hero-content">
            <div className="wh-cycle-hero-badge">
              <FontAwesomeIcon icon={faCalendarAlt} />
              Puberty &amp; Menstrual
            </div>
            <h1 className="wh-cycle-hero-title">{t('womensHealth:cycleTracker.title')}</h1>
            <p className="wh-cycle-hero-subtitle">
              Monitor your menstrual cycle, track symptoms, and get smart predictions for your next period, ovulation, and fertile window.
            </p>
          </div>
        </Container>
      </section>

      {/* Profile / Setup */}
      <section className="wh-cycle-profile-section">
        <Container>
          {loading ? <SkeletonCards /> : profile ? renderProfileSummary() : renderProfileSetup()}
        </Container>
      </section>

      {/* Stats Section */}
      <section className="wh-cycle-stats-section">
        <Container>
          {renderStats()}
        </Container>
      </section>

      {/* Main Content */}
      <Container>
        {/* Tabs */}
        <div className="wh-cycle-tabs-container">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`wh-cycle-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => {
                if (tab.key === 'log' && editingId === null) resetForm();
                setActiveTab(tab.key);
              }}
            >
              <FontAwesomeIcon icon={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="wh-cycle-tab-content">
          <div className="wh-cycle-section-header">
            <div className="wh-cycle-section-header-text">
              <h2>
                <FontAwesomeIcon icon={activeTab === 'cycles' ? faCalendarAlt : faPlus} />
                {activeTab === 'cycles' ? 'My Cycle History' : editingId !== null ? 'Edit Cycle' : 'Log Cycle'}
              </h2>
              <p>
                {activeTab === 'cycles'
                  ? 'View all your tracked menstrual cycles with predictions and insights.'
                  : editingId !== null
                    ? 'Update the details of this cycle entry.'
                    : 'Enter your cycle details to get accurate predictions.'}
              </p>
            </div>
            {activeTab === 'cycles' && (
              <button
                className="wh-cycle-btn wh-cycle-btn-primary"
                onClick={() => { resetForm(); setActiveTab('log'); }}
              >
                <FontAwesomeIcon icon={faPlus} /> {t('womensHealth:cycleTracker.logPeriod')}
              </button>
            )}
          </div>

          {activeTab === 'cycles' ? renderCycleCards() : renderForm()}
        </div>
      </Container>
    </div>
  );
};

export default CycleTracker;
